import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { useTrips } from "@/hooks/useTrips";
import { useVehicles } from "@/hooks/useVehicles";
import { format, parseISO, getDaysInMonth } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

const VEHICLE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--destructive))",
  "hsl(210, 70%, 55%)",
  "hsl(150, 60%, 45%)",
  "hsl(280, 60%, 55%)",
  "hsl(30, 80%, 55%)",
  "hsl(340, 70%, 55%)",
  "hsl(180, 60%, 45%)",
  "hsl(60, 70%, 45%)",
  "hsl(240, 50%, 60%)",
];

export const UtilizationChart = () => {
  const { trips, isLoading: tripsLoading } = useTrips();
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();

  const isLoading = tripsLoading || vehiclesLoading;

  const { chartData, vehicleNames } = useMemo(() => {
    if (!vehicles?.length || !trips?.length) return { chartData: [], vehicleNames: [] };

    // Filter to vehicles that have trips
    const vehiclesWithTrips = vehicles.filter((v) =>
      trips.some((t) => t.vehicle_id === v.id && t.trip_start)
    );

    if (!vehiclesWithTrips.length) return { chartData: [], vehicleNames: [] };

    // For each vehicle, calculate trip days per month
    const vehicleTripDaysByMonth: Record<string, Record<string, number>> = {};
    const allMonths = new Set<string>();

    for (const vehicle of vehiclesWithTrips) {
      const vTrips = trips.filter(
        (t) => t.vehicle_id === vehicle.id && t.trip_start && !t.trip_status?.toLowerCase().includes("cancel")
      );
      const byMonth: Record<string, number> = {};

      for (const trip of vTrips) {
        try {
          const date = parseISO(trip.trip_start!);
          const monthKey = format(date, "yyyy-MM");
          byMonth[monthKey] = (byMonth[monthKey] || 0) + (trip.trip_days || 1);
          allMonths.add(monthKey);
        } catch {
          // skip
        }
      }

      vehicleTripDaysByMonth[vehicle.id] = byMonth;
    }

    const sortedMonths = Array.from(allMonths).sort();
    const names = vehiclesWithTrips.map(
      (v) => v.name || `${v.year} ${v.make} ${v.model}`
    );

    const data = sortedMonths.map((monthKey) => {
      const daysInMonth = getDaysInMonth(parseISO(monthKey + "-01"));
      const entry: Record<string, string | number> = {
        month: format(parseISO(monthKey + "-01"), "MMM yyyy"),
      };

      vehiclesWithTrips.forEach((vehicle, idx) => {
        const tripDays = vehicleTripDaysByMonth[vehicle.id]?.[monthKey] || 0;
        const utilization = Math.min(Math.round((tripDays / daysInMonth) * 100), 100);
        entry[names[idx]] = utilization;
      });

      return entry;
    });

    return { chartData: data, vehicleNames: names };
  }, [vehicles, trips]);

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Vehicle Utilization</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Vehicle Utilization</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="month"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  color: "hsl(var(--popover-foreground))",
                }}
                formatter={(value: number, name: string) => [`${value}% — ${name}`, undefined]}
              />
              <Legend />
              {vehicleNames.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={VEHICLE_COLORS[idx % VEHICLE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No utilization data yet. Import trips from the Earnings tab.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
